import { RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, ApiError, isApiError } from '../../api';
import type { ConnectionDetail } from '../../api/areas/integrations';
import { DataError } from '../../components/DataError';
import { NavTabs } from '../../components/NavTabs';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { Loading } from '../../components/States';
import { useRetained } from '../../components/Write';
import { newRequestId } from '../../lib/ids';
import { useApi } from '../../lib/useApi';
import { CONNECTION_CHANGE_KINDS, PendingChangesPanel } from './changes';
import { CertificatesTab, CredentialsTab, EnvironmentTab, HistoryTab, OverviewTab, SettingsTab, TestsTab, type TabProps } from './connection/tabs';
import { Badge } from '../../components/Badge';
import { ConnectionHealthBadge } from './ConnectionsPage';
import { ChannelBadge, channelOf, useIntegrationContext, WithIntegrationContext } from './shared';

type TabId = 'overview' | 'environments' | 'credentials' | 'certificates' | 'settings' | 'tests' | 'history';

/** `usesSettings`: the tab shows the connection's settings (address, credentials, …). */
const TABS: { id: TabId; usesSettings: boolean; eppOnly?: boolean; component: (p: TabProps) => React.ReactNode }[] = [
  { id: 'overview', usesSettings: false, component: OverviewTab },
  { id: 'environments', usesSettings: true, component: EnvironmentTab },
  { id: 'credentials', usesSettings: true, component: CredentialsTab },
  { id: 'certificates', usesSettings: true, eppOnly: true, component: CertificatesTab },
  { id: 'settings', usesSettings: true, component: SettingsTab },
  { id: 'tests', usesSettings: true, eppOnly: true, component: TestsTab },
  { id: 'history', usesSettings: false, component: HistoryTab },
];

/** `/settings/registries/:code/:tab?` — one connection: overview, address, credentials,
 * certificates, connection settings, tests (connection checks, OT&E business test) and history.
 * The connection has one configuration: this server's (ADR-018); the tab id `environments` is
 * kept so old links keep working. */
export function ConnectionPage() {
  const { t } = useTranslation('integrations');
  const { code = '' } = useParams();
  return (
    <WithIntegrationContext>
      <ConnectionView key={code} code={code} crumbs={[{ label: t('connections.title'), to: '/settings/registries' }]} />
    </WithIntegrationContext>
  );
}

async function loadByCode(code: string, signal: AbortSignal): Promise<ConnectionDetail> {
  const list = await api.listConnections({ q: code }, { signal });
  const hit = list.items.find((c) => c.code === code);
  if (!hit) throw new ApiError({ status: 404, code: 'NOT_FOUND', message: `No connection with code "${code}"`, requestId: newRequestId() });
  return api.getConnection(hit.id, { signal });
}

function ConnectionView({ code, crumbs }: { code: string; crumbs: { label: string; to?: string }[] }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const { tab = 'overview' } = useParams();
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);
  const result = useApi((signal) => loadByCode(code, signal), [code, tick]);
  const connection = useRetained(result.data);
  // The server this Control Center runs on; it only labels the settings (and asks for a second
  // person on production).
  const env = ctx.integration_environment;
  const active = TABS.find((x) => x.id === tab);
  const perEnv = !!active?.usesSettings;
  const id = connection?.id;
  const envResult = useApi((signal) => (perEnv && id ? api.getConnectionEnvironment(id, { signal }) : Promise.resolve(undefined)), [perEnv, id, tick]);
  const envDetail = useRetained(envResult.data);
  useDocumentTitle(connection?.name ?? code);

  if (result.status === 'error' && !connection) {
    const notFound = isApiError(result.error) && result.error.status === 404;
    return (
      <>
        <PageHeader title={code} crumbs={crumbs} />
        <DataError error={result.error} title={notFound ? t('connection.notFound') : t('connection.loadFailed')} onRetry={notFound ? undefined : result.reload} />
      </>
    );
  }
  if (!connection) {
    return (
      <>
        <PageHeader title={code} crumbs={crumbs} />
        <Loading label={t('connection.loading')} rows={4} />
      </>
    );
  }
  const base = `/settings/registries/${encodeURIComponent(connection.code)}`;
  const visibleTabs = TABS.filter((x) => !x.eppOnly || connection.protocol === 'EPP');
  if (!active || !visibleTabs.includes(active)) return <Navigate to={base} replace />;
  const tabLink = (tabId: string) => (tabId === 'overview' ? base : `${base}/${tabId}`);
  const Tab = active.component;

  return (
    <>
      <PageHeader
        title={connection.name}
        crumbs={[...crumbs, { label: connection.name }]}
        // ID line: code · kind · accreditation of a partner registrar.
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <code className="font-mono">{connection.code}</code>
            <span aria-hidden="true">·</span>
            <span>{t(`kind.${connection.kind}`)}</span>
            {connection.upstream ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{t('routing.registrars.iana', { id: connection.upstream.iana_id, name: connection.upstream.trade_name })}</span>
              </>
            ) : null}
          </span>
        }
        badges={
          <>
            <ConnectionHealthBadge connection={connection} />
            <ChannelBadge channel={channelOf(connection.kind, connection.protocol)} />
            <Badge tone={connection.role === 'UPSTREAM_REGISTRAR' ? 'pending' : 'info'} icon={false}>
              {t(`role.value.${connection.role ?? 'REGISTRAR_OF_RECORD'}`)}
            </Badge>
          </>
        }
        actions={
          <Button type="button" variant="outline" size="sm" onClick={refresh}>
            <RefreshCw /> {t('common.refresh')}
          </Button>
        }
      />
      {connection.open_changes ? (
        <div className="mb-6">
          <PendingChangesPanel kinds={CONNECTION_CHANGE_KINDS} connectionId={connection.id} refreshKey={tick} onChanged={refresh} />
        </div>
      ) : null}
      <NavTabs className="mb-4" label={t('connection.sections')} active={active.id} tabs={visibleTabs.map((x) => ({ id: x.id, label: t(`connection.tab.${x.id}`), to: tabLink(x.id) }))} />
      {perEnv && envResult.status === 'error' ? <DataError error={envResult.error} onRetry={envResult.reload} title={t('connection.envLoadFailed')} /> : null}
      <Tab connection={connection} env={env} detail={envDetail && envDetail.connection.id === connection.id ? envDetail : undefined} refresh={refresh} tabLink={tabLink} />
    </>
  );
}
